import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Star,
  Heart,
  Users as UsersIcon,
  CreditCard,
  Briefcase,
  Film,
  Eye,
  ThumbsUp,
  Clock,
  MapPin,
  Shield,
  CheckCircle,
  XCircle,
  Globe,
  Hash,
  Link2,
  DollarSign,
  FileText,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Award,
  Video,
  Image,
  ExternalLink,
  RefreshCw,
  Activity,
  BookOpen,
  Smartphone,
  Share2,
  Bell,
  Zap,
  Info,
  X,
  MessageSquare,
  Repeat,
  AlertCircle,
  PlayCircle,
  StopCircle,
  UserCheck,
  UserX,
  Mic,
  Gift,
  Headphones,
  Copy,
  Ban,
  LifeBuoy,
  Banknote,
  BookMarked,
  Tag,
  ChevronUp,
  Home,
  ShieldCheck,
  GraduationCap,
  Flag,
} from 'lucide-react';
import { useUserDetail, useUserBookings, useUserContent } from '../hooks/useUsers';
import {
  UserBookingItem,
  UserContentItem,
  UserDetailService,
  BookingDetail as BookingDetailType,
  TimelineEvent,
} from '../types/user';
import {
  cn,
  formatRelativeTime,
  formatDateTime,
  formatNumber,
  formatCents,
  formatCentsDecimal,
  formatPhoneNumber,
  formatCompactNumber,
  parseDateOnly,
  showToast,
} from '../utils/helpers';
import { regenerateVideo } from '../services/users';
import { Avatar, Badge } from '../components/ui';
import {
  HouseholdTab,
  VerificationTab,
  AddressesTab,
  CredentialsTab,
  AvailabilityTab,
  ReportsTab,
  SocialTab,
  BadgesTab,
  NotificationsTab,
  ReferralsTab,
  XuBuxTab,
  CommunicationTab,
  EngagementTab,
  LocationTab,
  DevicesAuthTab,
  RecordingsTab,
  GroupSessionsTab,
  ReviewsTab,
  FinancialTab,
} from '../components/user-tabs';

// ============================================================================
// Constants & Types
// ============================================================================

type TabKey =
  | 'overview' | 'bookings' | 'content' | 'services' | 'financial'
  | 'household' | 'availability' | 'verification' | 'credentials' | 'addresses'
  | 'reports' | 'social' | 'referrals' | 'badges' | 'notifications'
  | 'xubux' | 'communication' | 'engagement' | 'location' | 'devices' | 'recordings'
  | 'groups' | 'reviews';

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ElementType;
}

const TABS: TabDef[] = [
  { key: 'overview', label: 'Overview', icon: User },
  { key: 'bookings', label: 'Bookings', icon: Calendar },
  { key: 'content', label: 'Content', icon: Film },
  { key: 'services', label: 'Services', icon: Briefcase },
  { key: 'financial', label: 'Financial', icon: DollarSign },
  { key: 'household', label: 'Household', icon: Home },
  { key: 'groups', label: 'Group Sessions', icon: UsersIcon },
  { key: 'availability', label: 'Availability', icon: Clock },
  { key: 'verification', label: 'Verification', icon: ShieldCheck },
  { key: 'credentials', label: 'Credentials', icon: GraduationCap },
  { key: 'addresses', label: 'Addresses', icon: MapPin },
  { key: 'reports', label: 'Reports', icon: Flag },
  { key: 'reviews', label: 'Reviews', icon: Star },
  { key: 'social', label: 'Social', icon: Heart },
  { key: 'referrals', label: 'Referrals', icon: Share2 },
  { key: 'badges', label: 'Badges', icon: Award },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'xubux', label: 'XuBux', icon: Zap },
  { key: 'communication', label: 'Chat & Calls', icon: MessageSquare },
  { key: 'engagement', label: 'Engagement', icon: Eye },
  { key: 'location', label: 'Location', icon: Globe },
  { key: 'devices', label: 'Devices & Auth', icon: Smartphone },
  { key: 'recordings', label: 'Recordings', icon: Mic },
];

// ============================================================================
// Section Card Wrapper
// ============================================================================

interface SectionCardProps {
  title: string;
  icon: React.ElementType;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon: Icon,
  subtitle,
  children,
  className,
  collapsible = false,
  defaultOpen = true,
  actions,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={cn('card overflow-hidden', className)}>
      <button
        onClick={collapsible ? () => setOpen(!open) : undefined}
        className={cn(
          'w-full flex items-center justify-between px-5 py-4 border-b border-border/50',
          collapsible && 'cursor-pointer hover:bg-muted/20 transition-colors'
        )}
      >
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {collapsible && (
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                !open && '-rotate-90'
              )}
            />
          )}
        </div>
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
};

// ============================================================================
// Info Row Component
// ============================================================================

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ElementType;
  mono?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, icon: Icon, mono }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-border/30 last:border-0">
    {Icon && (
      <div className="mt-0.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={cn('text-sm text-foreground mt-0.5', mono && 'font-mono text-xs')}>
        {value || <span className="text-muted-foreground/50">N/A</span>}
      </p>
    </div>
  </div>
);

// ============================================================================
// Metric Card
// ============================================================================

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  subtitle,
}) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-4 w-4', color)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <p className="text-lg font-bold text-foreground">{value}</p>
        {subtitle && (
          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Booking Status Badge
// ============================================================================

const getBookingStatusVariant = (
  status: string
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'COMPLETED':
    case 'RECAP':
      return 'success';
    case 'CONFIRMED':
    case 'IN_PROGRESS':
      return 'info';
    case 'PENDING':
    case 'PENDING_PAYMENT':
    case 'PENDING_CAPTURE':
    case 'AWAITING_PROVIDER':
      return 'warning';
    case 'CANCELLED':
    case 'NO_SHOW':
    case 'PAYMENT_FAILED':
    case 'PAYMENT_UNCERTAIN':
      return 'error';
    default:
      return 'default';
  }
};

// ============================================================================
// Content Status Badge
// ============================================================================

const getContentStatusVariant = (
  status: string
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'READY':
      return 'success';
    case 'PENDING_REVIEW':
    case 'PENDING_IMPORT':
      return 'warning';
    case 'FAILED':
    case 'REJECTED':
      return 'error';
    case 'PROCESSING':
    case 'UPLOADING':
      return 'info';
    default:
      return 'default';
  }
};

// ============================================================================
// Booking Row (clickable)
// ============================================================================

const BookingRow: React.FC<{
  booking: UserBookingItem;
  isSelected: boolean;
  onClick: () => void;
}> = ({ booking, isSelected, onClick }) => (
  <div
    onClick={onClick}
    className={cn(
      'flex items-center gap-4 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/10 transition-colors cursor-pointer',
      isSelected && 'bg-primary/5 border-l-2 border-l-primary'
    )}
  >
    {/* Counterparty avatar */}
    <Avatar
      src={booking.counterparty.profileImage || undefined}
      name={booking.counterparty.name || 'User'}
      size="sm"
    />

    {/* Main info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground truncate">
          {booking.service?.serviceName || 'No service'}
        </span>
        <Badge
          variant={booking.role === 'customer' ? 'info' : 'default'}
          size="sm"
        >
          {booking.role === 'customer' ? 'Customer' : 'Provider'}
        </Badge>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-xs text-muted-foreground">
          with {booking.counterparty.name || 'Unknown'}
        </span>
        {booking.serviceMode && (
          <span className="text-[10px] text-muted-foreground/70">
            {booking.serviceMode}
          </span>
        )}
      </div>
    </div>

    {/* Status */}
    <Badge variant={getBookingStatusVariant(booking.status)} size="sm">
      {booking.status.replace(/_/g, ' ')}
    </Badge>

    {/* Amount */}
    <div className="text-right flex-shrink-0 w-20">
      <p className="text-sm font-medium text-foreground">
        {booking.isFree ? 'Free' : formatCentsDecimal(booking.totalAmountCents)}
      </p>
      <p className="text-[10px] text-muted-foreground">
        {booking.duration}min
      </p>
    </div>

    {/* Rating */}
    <div className="flex-shrink-0 w-14 text-right">
      {booking.rating != null ? (
        <div className="flex items-center gap-1 justify-end">
          <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
          <span className="text-xs font-medium">{booking.rating.toFixed(1)}</span>
        </div>
      ) : (
        <span className="text-[10px] text-muted-foreground">--</span>
      )}
    </div>

    {/* Date */}
    <div className="flex-shrink-0 w-28 text-right">
      <p className="text-xs text-muted-foreground">
        {formatRelativeTime(booking.bookingDate)}
      </p>
      <p className="text-[10px] text-muted-foreground/60">
        {new Date(booking.bookingDate).toLocaleDateString()}
      </p>
    </div>
  </div>
);

// ============================================================================
// Booking Detail Modal - Tab-based layout
// ============================================================================

type BookingModalTab = 'overview' | 'payment' | 'timeline' | 'session' | 'reviews' | 'safety' | 'support' | 'referrals';

const BOOKING_STATUS_COLORS: Record<string, { text: string; bg: string }> = {
  PENDING: { text: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/15' },
  PENDING_PAYMENT: { text: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/15' },
  PENDING_CAPTURE: { text: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/15' },
  AWAITING_PROVIDER: { text: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-500/15' },
  CONFIRMED: { text: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-500/15' },
  IN_PROGRESS: { text: 'text-indigo-700 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-500/15' },
  COMPLETED: { text: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-500/15' },
  RECAP: { text: 'text-purple-700 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-500/15' },
  CANCELLED: { text: 'text-gray-700 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-500/15' },
  CANCELLING: { text: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-500/15' },
  REFUND_PENDING: { text: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-500/15' },
  REFUND_UNCERTAIN: { text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/15' },
  PAYMENT_FAILED: { text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/15' },
  PAYMENT_UNCERTAIN: { text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/15' },
  NO_SHOW: { text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/15' },
  RESCHEDULED: { text: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-500/15' },
};

function buildTimeline(booking: BookingDetailType): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  events.push({ timestamp: booking.createdAt, type: 'booking', icon: 'plus-circle', label: 'Booking created', severity: 'info' });

  if (booking.acceptanceDeadline) {
    events.push({ timestamp: booking.acceptanceDeadline, type: 'booking', icon: 'clock', label: 'Acceptance deadline', severity: 'warning' });
  }

  for (const payment of booking.payments) {
    events.push({ timestamp: payment.createdAt, type: 'payment', icon: 'credit-card', label: `Payment initiated ($${payment.amount.toFixed(2)})`, details: `${payment.cardBrand ?? 'card'} ****${payment.cardLast4 ?? '????'}`, severity: 'info' });
    if (payment.paidAt) {
      events.push({ timestamp: payment.paidAt, type: 'payment', icon: 'check-circle', label: `Payment captured ($${payment.amount.toFixed(2)})`, severity: 'success' });
    }
    if (payment.failureReason) {
      events.push({ timestamp: payment.updatedAt, type: 'payment', icon: 'x-circle', label: `Payment failed: ${payment.failureReason}`, severity: 'error' });
    }
    if (payment.refundedAt) {
      events.push({ timestamp: payment.refundedAt, type: 'payment', icon: 'arrow-uturn-left', label: `Refund issued ($${payment.refundAmount?.toFixed(2)})`, details: payment.refundReason ?? undefined, severity: 'warning' });
    }
  }

  if (booking.lastRescheduledAt) {
    events.push({ timestamp: booking.lastRescheduledAt, type: 'booking', icon: 'calendar', label: `Rescheduled by user #${booking.lastRescheduledBy}`, severity: 'info' });
  }

  if (booking.virtualRoomNotifiedAt) {
    events.push({ timestamp: booking.virtualRoomNotifiedAt, type: 'session', icon: 'bell', label: 'Session reminder sent', severity: 'info' });
  }

  for (const vse of booking.videoSessionEvents) {
    events.push({ timestamp: vse.eventTime, type: 'session', icon: vse.eventType === 'JOINED' ? 'arrow-right-on-rectangle' : 'arrow-left-on-rectangle', label: `${vse.participantName ?? vse.participantRole} ${vse.eventType.toLowerCase()}`, severity: vse.eventType === 'JOINED' ? 'success' : 'info' });
  }

  if (booking.sessionMonitoring) {
    const sm = booking.sessionMonitoring;
    if (sm.startedAt) {
      events.push({ timestamp: sm.startedAt, type: 'session', icon: 'microphone', label: 'Audio monitoring started', severity: 'info' });
    }
    if (sm.stoppedAt) {
      events.push({ timestamp: sm.stoppedAt, type: 'session', icon: 'stop', label: `Audio monitoring stopped (${sm.status})`, severity: sm.status === 'FAILED' ? 'error' : 'info' });
    }
  }

  for (const flag of booking.safetyFlags) {
    events.push({ timestamp: flag.createdAt, type: 'safety', icon: 'shield-exclamation', label: `Safety flag: ${flag.category} (${flag.severity})`, details: flag.message, severity: flag.severity === 'RED' ? 'error' : 'warning' });
  }

  if (booking.completedAt) {
    events.push({ timestamp: booking.completedAt, type: 'booking', icon: 'check-circle', label: 'Booking completed', severity: 'success' });
  }

  if (booking.recapSubmittedAt) {
    events.push({ timestamp: booking.recapSubmittedAt, type: 'booking', icon: 'document-text', label: 'Session recap submitted', severity: 'info' });
  }

  if (booking.recordingSavedAt) {
    events.push({ timestamp: booking.recordingSavedAt, type: 'session', icon: 'film', label: 'Recording saved', severity: 'success' });
  }

  if (booking.providerRatedAt) {
    events.push({ timestamp: booking.providerRatedAt, type: 'review', icon: 'star', label: `Customer reviewed provider (${booking.providerRating}/5)`, severity: 'info' });
  }
  if (booking.customerRatedAt) {
    events.push({ timestamp: booking.customerRatedAt, type: 'review', icon: 'star', label: `Provider reviewed customer (${booking.customerRating}/5)`, severity: 'info' });
  }

  if (booking.cancelledAt) {
    events.push({ timestamp: booking.cancelledAt, type: 'booking', icon: 'x-circle', label: `Booking cancelled (${booking.cancellationType})`, details: booking.cancellationReason ?? undefined, severity: 'error' });
  }

  for (const payout of booking.payouts) {
    if (payout.submittedAt) {
      events.push({ timestamp: payout.submittedAt, type: 'payout', icon: 'banknotes', label: `Payout submitted ($${payout.netAmount.toFixed(2)})`, severity: 'info' });
    }
    if (payout.paidAt) {
      events.push({ timestamp: payout.paidAt, type: 'payout', icon: 'banknotes', label: `Payout completed ($${payout.netAmount.toFixed(2)})`, severity: 'success' });
    }
    if (payout.failedAt) {
      events.push({ timestamp: payout.failedAt, type: 'payout', icon: 'exclamation-triangle', label: `Payout failed: ${payout.failureMessage}`, severity: 'error' });
    }
  }

  for (const ticket of booking.supportTickets) {
    events.push({ timestamp: ticket.createdAt, type: 'support', icon: 'chat-bubble', label: `Support ticket: ${ticket.title}`, details: `${ticket.category} / ${ticket.priority}`, severity: ticket.priority === 'URGENT' ? 'error' : 'info' });
  }

  events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  return events;
}

function formatDollars(amount: number | null | undefined): string {
  if (amount == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatHHMMSS(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatMsToTimestamp(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '--';
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

const severityColor = (s: string) => {
  switch (s) {
    case 'error': return 'bg-red-500';
    case 'warning': return 'bg-amber-500';
    case 'success': return 'bg-green-500';
    default: return 'bg-blue-500';
  }
};

const safetyBadgeVariant = (severity: string): 'error' | 'warning' | 'default' => {
  if (severity === 'RED') return 'error';
  if (severity === 'ORANGE') return 'warning';
  return 'warning';
};

const paymentStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'COMPLETED': return 'success';
    case 'PAID': return 'success';
    case 'FAILED': return 'error';
    case 'REFUNDED': return 'warning';
    case 'PARTIALLY_REFUNDED': return 'warning';
    case 'PENDING': return 'warning';
    case 'PROCESSING': return 'info';
    default: return 'default';
  }
};

const payoutStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'PAID': return 'success';
    case 'FAILED': return 'error';
    case 'CANCELLED': return 'default';
    case 'SUBMITTED': case 'IN_TRANSIT': return 'info';
    default: return 'warning';
  }
};

// --- Person Card (reusable for customer/provider/beneficiary) ---
const PersonCard: React.FC<{
  person: { id: number; name: string | null; email?: string | null; phoneNumber?: string | null; profileImage: string | null; isActive?: boolean; isVerified?: boolean };
  role: string;
  roleBadgeVariant: 'info' | 'default' | 'warning';
  extraInfo?: React.ReactNode;
  onViewUser: (id: number) => void;
}> = ({ person, role, roleBadgeVariant, extraInfo, onViewUser }) => (
  <div className="bg-muted/10 rounded-lg p-4">
    <div className="flex items-center gap-3 mb-3">
      <Avatar src={person.profileImage || undefined} name={person.name || role} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">{person.name || 'Unknown'}</span>
          <Badge variant={roleBadgeVariant} size="sm">{role}</Badge>
          {person.isActive === false && <Badge variant="error" size="sm">Inactive</Badge>}
          {person.isVerified === false && person.isActive !== undefined && <Badge variant="warning" size="sm">Unverified</Badge>}
        </div>
        {person.email && <p className="text-xs text-muted-foreground truncate">{person.email}</p>}
      </div>
      <button onClick={() => onViewUser(person.id)} className="p-1.5 rounded-md hover:bg-muted/30 transition-colors" title="View user">
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
    {person.phoneNumber && (
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        <Phone className="h-3 w-3" />
        <span>{person.phoneNumber}</span>
      </div>
    )}
    {extraInfo}
  </div>
);

// ============================================================================
// Send Notification Modal
// ============================================================================

const NOTIFICATION_CHANNELS: { key: import('../types/notifications').NotificationChannel; label: string; icon: React.ElementType }[] = [
  { key: 'PUSH', label: 'Push', icon: Smartphone },
  { key: 'IN_APP', label: 'In-App', icon: Bell },
  { key: 'SMS', label: 'SMS', icon: MessageSquare },
  { key: 'EMAIL', label: 'Email', icon: Mail },
];

const SendNotificationModal: React.FC<{
  userId: number;
  userName: string | null;
  onClose: () => void;
}> = ({ userId, userName, onClose }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [channels, setChannels] = useState<import('../types/notifications').NotificationChannel[]>(['PUSH', 'IN_APP']);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const toggleChannel = (ch: import('../types/notifications').NotificationChannel) => {
    setChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]
    );
  };

  const canSend = title.trim() && body.trim() && channels.length > 0;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const { sendToUser } = await import('../services/notifications');
      await sendToUser({
        userId,
        channels,
        title: title.trim(),
        body: body.trim(),
      });
      showToast('success', `Notification sent to ${userName || `User #${userId}`}`);
      setSent(true);
      setTimeout(onClose, 1200);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send notification';
      showToast('error', message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-background border border-border shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10">
              <Bell className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">Send Notification</h3>
              <p className="text-[11px] text-muted-foreground">
                To {userName || 'User'} #{userId}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/30 transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Channels */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Channels</label>
            <div className="flex gap-2">
              {NOTIFICATION_CHANNELS.map((ch) => (
                <button
                  key={ch.key}
                  onClick={() => toggleChannel(ch.key)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                    channels.includes(ch.key)
                      ? 'bg-primary/15 text-primary border-primary/30'
                      : 'text-muted-foreground border-border hover:bg-muted/20'
                  )}
                >
                  <ch.icon className="h-3 w-3" />
                  {ch.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title..."
              className="w-full px-3 py-2 rounded-lg border border-border bg-muted/10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification body..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-border bg-muted/10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend || sending || sent}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors',
              sent
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {sending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Sending...
              </>
            ) : sent ? (
              <>
                <CheckCircle className="h-3 w-3" />
                Sent!
              </>
            ) : (
              <>
                <Bell className="h-3 w-3" />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const BookingDetailModal: React.FC<{
  booking: UserBookingItem;
  fullDetail: BookingDetailType | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onViewUser: (userId: number) => void;
}> = ({ booking, fullDetail, loading, error, onClose, onViewUser }) => {
  const b = fullDetail;
  const hasFullData = !!b;
  const [activeTab, setActiveTab] = useState<BookingModalTab>('overview');

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const timeline = useMemo(() => (b ? buildTimeline(b) : []), [b]);

  // Determine which tabs to show
  const tabs = useMemo(() => {
    const list: { key: BookingModalTab; label: string; icon: React.ElementType; count?: number }[] = [
      { key: 'overview', label: 'Overview', icon: User },
      { key: 'payment', label: 'Payment', icon: CreditCard },
      { key: 'timeline', label: 'Timeline', icon: Activity, count: timeline.length },
      { key: 'session', label: 'Session', icon: Video },
      { key: 'reviews', label: 'Reviews', icon: Star },
    ];
    if (b && (b.safetyFlags.length > 0 || b.userReports.length > 0 || b.customerNoShow)) {
      list.push({ key: 'safety', label: 'Safety', icon: Shield, count: (b.safetyFlags.length + b.userReports.length) });
    }
    if (b && b.supportTickets.length > 0) {
      list.push({ key: 'support', label: 'Support', icon: LifeBuoy, count: b.supportTickets.length });
    }
    if (b && (b.creditsUsed.length > 0 || b.creditUsages.length > 0 || b.qualifiedReferral)) {
      list.push({ key: 'referrals', label: 'Referrals', icon: Gift });
    }
    return list;
  }, [b, timeline.length]);

  const statusColors = BOOKING_STATUS_COLORS[booking.status] || { text: 'text-foreground', bg: 'bg-muted/20' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl max-h-[92vh] rounded-2xl bg-background border border-border shadow-2xl overflow-hidden flex flex-col"
      >
        {/* ================================================================ */}
        {/* HEADER - Always visible */}
        {/* ================================================================ */}
        <div className="flex-shrink-0 border-b border-border bg-muted/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-xl bg-primary/10 flex-shrink-0">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-foreground">Booking #{booking.id}</h2>
                  <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium border', statusColors.text, statusColors.bg)}>
                    {booking.status.replace(/_/g, ' ')}
                  </span>
                  {booking.serviceMode && (
                    <Badge variant={booking.serviceMode === 'VIRTUAL' ? 'info' : 'default'} size="sm">
                      {booking.serviceMode.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                  <span>{booking.service?.serviceName || 'No service'}</span>
                  <span>{new Date(booking.bookingDate).toLocaleDateString()}</span>
                  <span>{new Date(booking.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} - {new Date(booking.endTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                  <span>{booking.duration}min</span>
                  <span className="font-medium text-foreground">
                    {booking.isFree ? 'FREE' : formatCentsDecimal(booking.totalAmountCents)}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted/30 transition-colors flex-shrink-0">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          {/* Tab bar */}
          <div className="flex gap-0 px-6 overflow-x-auto">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                    activeTab === tab.key
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  )}
                >
                  <TabIcon className="h-3.5 w-3.5" />
                  {tab.label}
                  {tab.count != null && tab.count > 0 && (
                    <span className="ml-1 px-1.5 py-0 rounded-full bg-muted/30 text-[10px]">{tab.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ================================================================ */}
        {/* LOADING / ERROR */}
        {/* ================================================================ */}
        {loading && (
          <div className="flex items-center gap-2 px-6 py-3 bg-primary/5 border-b border-primary/20 flex-shrink-0">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm text-primary">Loading full booking details...</span>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 px-6 py-3 bg-amber-500/10 border-b border-amber-500/20 flex-shrink-0">
            <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-400">Could not load full detail</p>
              <p className="text-xs text-amber-400/70 mt-0.5">Showing data from the list view.</p>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Participants */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Participants</h3>
                </div>
                <div className="p-5 space-y-4">
                  {/* Customer */}
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={hasFullData ? (b.customer.profileImage || undefined) : (booking.role === 'customer' ? undefined : (booking.counterparty.profileImage || undefined))}
                      name={hasFullData ? (b.customer.name || 'Customer') : (booking.role === 'customer' ? 'This User' : (booking.counterparty.name || 'Customer'))}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {hasFullData ? (b.customer.name || 'Unknown') : (booking.role === 'customer' ? 'This User' : (booking.counterparty.name || 'Unknown'))}
                        </span>
                        <Badge variant="info" size="sm">Customer</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {hasFullData ? b.customer.email : (booking.role === 'provider' ? booking.counterparty.email : null) || 'N/A'}
                      </p>
                    </div>
                    {hasFullData && (
                      <button
                        onClick={() => onViewUser(b.customer.id)}
                        className="p-1.5 rounded-md hover:bg-muted/30 transition-colors"
                        title="View user"
                      >
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    )}
                  </div>

                  {/* Provider */}
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={hasFullData ? (b.provider.profileImage || undefined) : (booking.role === 'provider' ? undefined : (booking.counterparty.profileImage || undefined))}
                      name={hasFullData ? (b.provider.name || 'Provider') : (booking.role === 'provider' ? 'This User' : (booking.counterparty.name || 'Provider'))}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {hasFullData ? (b.provider.name || 'Unknown') : (booking.role === 'provider' ? 'This User' : (booking.counterparty.name || 'Unknown'))}
                        </span>
                        <Badge variant="default" size="sm">Provider</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {hasFullData ? b.provider.email : (booking.role === 'customer' ? booking.counterparty.email : null) || 'N/A'}
                      </p>
                    </div>
                    {hasFullData && (
                      <button
                        onClick={() => onViewUser(b.provider.id)}
                        className="p-1.5 rounded-md hover:bg-muted/30 transition-colors"
                        title="View user"
                      >
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    )}
                  </div>

                  {/* Beneficiary (if combo) */}
                  {hasFullData && b.beneficiary && (
                    <div className="flex items-center gap-3">
                      <Avatar
                        src={b.beneficiary.profileImage || undefined}
                        name={b.beneficiary.name || 'Beneficiary'}
                        size="md"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">
                            {b.beneficiary.name || 'Unknown'}
                          </span>
                          <Badge variant="warning" size="sm">Beneficiary</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {b.beneficiary.email || 'N/A'}
                        </p>
                      </div>
                      <button
                        onClick={() => onViewUser(b.beneficiary!.id)}
                        className="p-1.5 rounded-md hover:bg-muted/30 transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Schedule & Service */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Schedule & Service</h3>
                </div>
                <div className="p-5">
                  <InfoRow label="Service" value={booking.service?.serviceName || 'N/A'} icon={Briefcase} />
                  <InfoRow label="Booking Type" value={booking.bookingType.replace(/_/g, ' ')} icon={Calendar} />
                  <InfoRow
                    label="Service Mode"
                    value={booking.serviceMode?.replace(/_/g, ' ') || 'N/A'}
                    icon={booking.serviceMode === 'VIRTUAL' ? Globe : MapPin}
                  />
                  {hasFullData && b.isInstant && (
                    <InfoRow label="Instant Booking" value={<Badge variant="info" size="sm">Instant</Badge>} icon={Zap} />
                  )}
                  <InfoRow label="Booking Date" value={new Date(booking.bookingDate).toLocaleDateString()} icon={Calendar} />
                  <InfoRow
                    label="Time"
                    value={`${new Date(booking.startTime).toLocaleTimeString()} - ${new Date(booking.endTime).toLocaleTimeString()}`}
                    icon={Clock}
                  />
                  <InfoRow label="Duration" value={`${booking.duration} minutes`} icon={Clock} />
                  {hasFullData && b.acceptanceDeadline && (
                    <InfoRow label="Acceptance Deadline" value={formatDateTime(b.acceptanceDeadline)} icon={AlertTriangle} />
                  )}
                  {hasFullData && b.comboSize != null && b.comboSize > 1 && (
                    <InfoRow label="Combo Size" value={`${b.comboSize} people`} icon={UsersIcon} />
                  )}
                  {booking.completedAt && (
                    <InfoRow label="Completed" value={formatDateTime(booking.completedAt)} icon={CheckCircle} />
                  )}
                  <InfoRow label="Booked On" value={formatDateTime(booking.createdAt)} icon={Clock} />
                </div>
              </div>

              {/* Location (full detail only) */}
              {hasFullData && (b.address || b.city) && (
                <div className="card overflow-hidden">
                  <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Location</h3>
                  </div>
                  <div className="p-5">
                    {b.address && <InfoRow label="Address" value={b.address} icon={MapPin} />}
                    {b.city && <InfoRow label="City" value={b.city} icon={MapPin} />}
                    {b.state && <InfoRow label="State" value={b.state} icon={MapPin} />}
                    {b.zipCode && <InfoRow label="Zip Code" value={b.zipCode} icon={Hash} />}
                    {b.distanceKm != null && (
                      <InfoRow label="Distance" value={`${b.distanceKm.toFixed(1)} km`} icon={MapPin} />
                    )}
                  </div>
                </div>
              )}

              {/* Notes & Recap */}
              {(booking.review || (hasFullData && (b.notes || b.sessionRecap))) && (
                <div className="card overflow-hidden">
                  <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Notes & Recap</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    {hasFullData && b.notes && (
                      <div>
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">Booking Notes</p>
                        <p className="text-sm text-foreground bg-muted/10 p-3 rounded-lg">{b.notes}</p>
                      </div>
                    )}
                    {hasFullData && b.sessionRecap && (
                      <div>
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">
                          Session Recap
                          {b.recapSubmittedAt && (
                            <span className="text-muted-foreground/60 ml-2 normal-case">
                              submitted {formatRelativeTime(b.recapSubmittedAt)}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-foreground bg-muted/10 p-3 rounded-lg">{b.sessionRecap}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Payment */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Payment</h3>
                </div>
                <div className="p-5">
                  <InfoRow
                    label="Amount"
                    value={booking.isFree ? 'Free' : formatCentsDecimal(booking.totalAmountCents)}
                    icon={DollarSign}
                  />
                  {hasFullData && (
                    <>
                      <InfoRow
                        label="Payment Status"
                        value={
                          <Badge
                            variant={
                              b.paymentStatus === 'PAID' ? 'success' :
                              b.paymentStatus === 'FAILED' ? 'error' :
                              b.paymentStatus === 'REFUNDED' ? 'warning' : 'default'
                            }
                            size="sm"
                          >
                            {b.paymentStatus.replace(/_/g, ' ')}
                          </Badge>
                        }
                        icon={CreditCard}
                      />
                      <InfoRow label="Payment Required" value={b.paymentRequired ? 'Yes' : 'No'} icon={CreditCard} />
                      {b.paymentIntentId && (
                        <InfoRow label="Payment Intent" value={b.paymentIntentId} icon={Hash} mono />
                      )}
                    </>
                  )}
                  <InfoRow label="Free" value={booking.isFree ? 'Yes' : 'No'} icon={DollarSign} />

                  {/* Pricing Snapshot */}
                  {hasFullData && b.pricingSnapshot && (
                    <div className="mt-3 pt-3 border-t border-border/30">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">Pricing Breakdown</p>
                      <pre className="text-xs text-foreground bg-muted/10 p-3 rounded-lg overflow-x-auto">
                        {JSON.stringify(b.pricingSnapshot, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Payment records */}
                  {hasFullData && b.payments && b.payments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/30">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
                        Payment Records ({b.payments.length})
                      </p>
                      <div className="space-y-2">
                        {b.payments.map((p) => (
                          <div key={p.id} className="bg-muted/10 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <Badge variant={paymentStatusVariant(p.status)} size="sm">
                                {p.status}
                              </Badge>
                              <span className="text-sm font-medium">{formatDollars(p.amount)}</span>
                            </div>
                            {p.cardBrand && (
                              <p className="text-xs text-muted-foreground">{p.cardBrand} ****{p.cardLast4}</p>
                            )}
                            {p.stripeChargeId && (
                              <p className="text-[10px] text-muted-foreground/70 font-mono mt-1">{p.stripeChargeId}</p>
                            )}
                            {p.refundAmount != null && p.refundAmount > 0 && (
                              <p className="text-xs text-amber-400 mt-1">Refunded: {formatDollars(p.refundAmount)}</p>
                            )}
                            <p className="text-[10px] text-muted-foreground/50 mt-1">{formatDateTime(p.createdAt)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payout records */}
                  {hasFullData && b.payouts && b.payouts.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/30">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
                        Payouts ({b.payouts.length})
                      </p>
                      <div className="space-y-2">
                        {b.payouts.map((p) => (
                          <div key={p.id} className="bg-muted/10 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <Badge variant={payoutStatusVariant(p.status)} size="sm">
                                {p.status}
                              </Badge>
                              <span className="text-sm font-medium">{formatDollars(p.amount)}</span>
                            </div>
                            {p.stripeTransferId && (
                              <p className="text-[10px] text-muted-foreground/70 font-mono">{p.stripeTransferId}</p>
                            )}
                            <p className="text-[10px] text-muted-foreground/50 mt-1">{formatDateTime(p.createdAt)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Reviews */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Reviews</h3>
                </div>
                <div className="p-5 space-y-4">
                  {/* Customer's review of provider */}
                  <div>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">Customer → Provider</p>
                    {booking.rating != null ? (
                      <div className="bg-muted/10 p-3 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={cn(
                                'h-4 w-4',
                                i < Math.round(booking.rating!) ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/20'
                              )}
                            />
                          ))}
                          <span className="text-sm font-medium ml-1">{booking.rating.toFixed(1)}</span>
                        </div>
                        {booking.review && (
                          <p className="text-sm text-foreground">{booking.review}</p>
                        )}
                        {hasFullData && (
                          <div className="flex gap-3 mt-2">
                            {b.providerReviewGoodFit != null && (
                              <div className="flex items-center gap-1">
                                {b.providerReviewGoodFit ? <UserCheck className="h-3 w-3 text-emerald-400" /> : <UserX className="h-3 w-3 text-red-400" />}
                                <span className="text-[10px] text-muted-foreground">Good Fit</span>
                              </div>
                            )}
                            {b.providerReviewWouldBook != null && (
                              <div className="flex items-center gap-1">
                                {b.providerReviewWouldBook ? <CheckCircle className="h-3 w-3 text-emerald-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                                <span className="text-[10px] text-muted-foreground">Would Book Again</span>
                              </div>
                            )}
                            {b.providerReviewWouldRefer != null && (
                              <div className="flex items-center gap-1">
                                {b.providerReviewWouldRefer ? <Share2 className="h-3 w-3 text-emerald-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                                <span className="text-[10px] text-muted-foreground">Would Refer</span>
                              </div>
                            )}
                          </div>
                        )}
                        {hasFullData && b.providerRatedAt && (
                          <p className="text-[10px] text-muted-foreground/50">Rated {formatRelativeTime(b.providerRatedAt)}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground/50">No review yet</p>
                    )}
                  </div>

                  {/* Provider's review of customer */}
                  <div>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">Provider → Customer</p>
                    {hasFullData && b.providerRating != null ? (
                      <div className="bg-muted/10 p-3 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={cn(
                                'h-4 w-4',
                                i < Math.round(b.providerRating!) ? 'text-purple-400 fill-purple-400' : 'text-muted-foreground/20'
                              )}
                            />
                          ))}
                          <span className="text-sm font-medium ml-1">{b.providerRating.toFixed(1)}</span>
                        </div>
                        {b.providerReview && (
                          <p className="text-sm text-foreground">{b.providerReview}</p>
                        )}
                        <div className="flex gap-3 mt-2">
                          {b.customerReviewGoodFit != null && (
                            <div className="flex items-center gap-1">
                              {b.customerReviewGoodFit ? <UserCheck className="h-3 w-3 text-emerald-400" /> : <UserX className="h-3 w-3 text-red-400" />}
                              <span className="text-[10px] text-muted-foreground">Good Fit</span>
                            </div>
                          )}
                          {b.customerReviewWouldServe != null && (
                            <div className="flex items-center gap-1">
                              {b.customerReviewWouldServe ? <CheckCircle className="h-3 w-3 text-emerald-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                              <span className="text-[10px] text-muted-foreground">Would Serve Again</span>
                            </div>
                          )}
                          {b.customerReviewWouldRefer != null && (
                            <div className="flex items-center gap-1">
                              {b.customerReviewWouldRefer ? <Share2 className="h-3 w-3 text-emerald-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                              <span className="text-[10px] text-muted-foreground">Would Refer</span>
                            </div>
                          )}
                        </div>
                        {b.customerRatedAt && (
                          <p className="text-[10px] text-muted-foreground/50">Rated {formatRelativeTime(b.customerRatedAt)}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground/50">
                        {hasFullData ? 'No review yet' : 'Requires full detail endpoint'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Cancellation (if applicable) */}
              {(booking.cancelledAt || (hasFullData && b.cancelledAt)) && (
                <div className="card overflow-hidden border-red-500/20">
                  <div className="px-5 py-3 border-b border-red-500/20 bg-red-500/5 flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-400" />
                    <h3 className="text-sm font-semibold text-red-400">Cancellation</h3>
                  </div>
                  <div className="p-5">
                    <InfoRow label="Cancelled At" value={formatDateTime(booking.cancelledAt || (hasFullData ? b.cancelledAt : null) || '')} icon={Clock} />
                    {hasFullData && b.cancellationType && (
                      <InfoRow label="Type" value={b.cancellationType.replace(/_/g, ' ')} icon={AlertTriangle} />
                    )}
                    {hasFullData && b.cancellationReason && (
                      <InfoRow label="Reason" value={b.cancellationReason} icon={FileText} />
                    )}
                    {hasFullData && b.cancelledBy != null && (
                      <InfoRow label="Cancelled By" value={`User #${b.cancelledBy}`} icon={User} />
                    )}
                  </div>
                </div>
              )}

              {/* Rescheduling (full detail) */}
              {hasFullData && (b.lastRescheduledAt || b.customerPreRescheduleUsed || b.providerPreRescheduleUsed) && (
                <div className="card overflow-hidden">
                  <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Rescheduling</h3>
                  </div>
                  <div className="p-5">
                    {b.lastRescheduledAt && (
                      <InfoRow label="Last Rescheduled" value={formatDateTime(b.lastRescheduledAt)} icon={Clock} />
                    )}
                    {b.lastRescheduledBy != null && (
                      <InfoRow label="Rescheduled By" value={`User #${b.lastRescheduledBy}`} icon={User} />
                    )}
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="bg-muted/10 p-2 rounded-lg text-center">
                        <p className="text-[10px] text-muted-foreground">Customer Pre</p>
                        <p className={cn('text-xs font-medium', b.customerPreRescheduleUsed ? 'text-amber-400' : 'text-emerald-400')}>
                          {b.customerPreRescheduleUsed ? 'Used' : 'Available'}
                        </p>
                      </div>
                      <div className="bg-muted/10 p-2 rounded-lg text-center">
                        <p className="text-[10px] text-muted-foreground">Customer Post</p>
                        <p className={cn('text-xs font-medium', b.customerPostRescheduleUsed ? 'text-amber-400' : 'text-emerald-400')}>
                          {b.customerPostRescheduleUsed ? 'Used' : 'Available'}
                        </p>
                      </div>
                      <div className="bg-muted/10 p-2 rounded-lg text-center">
                        <p className="text-[10px] text-muted-foreground">Provider Pre</p>
                        <p className={cn('text-xs font-medium', b.providerPreRescheduleUsed ? 'text-amber-400' : 'text-emerald-400')}>
                          {b.providerPreRescheduleUsed ? 'Used' : 'Available'}
                        </p>
                      </div>
                      <div className="bg-muted/10 p-2 rounded-lg text-center">
                        <p className="text-[10px] text-muted-foreground">Provider Post</p>
                        <p className={cn('text-xs font-medium', b.providerPostRescheduleUsed ? 'text-amber-400' : 'text-emerald-400')}>
                          {b.providerPostRescheduleUsed ? 'Used' : 'Available'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* No-Show */}
              {booking.customerNoShow && (
                <div className="card overflow-hidden border-amber-500/20">
                  <div className="px-5 py-3 bg-amber-500/5 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <span className="text-sm font-semibold text-amber-400">Customer No-Show</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* ---- SESSION TAB ---- */}
          {activeTab === 'session' && b && (
          <div className="space-y-6">
          {b.serviceMode === 'VIRTUAL' && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                <Video className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Virtual Session</h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  {b.meetingURL && <InfoRow label="Meeting URL" value={<a href={b.meetingURL} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{b.meetingURL}</a>} icon={Link2} />}
                  {b.videoRecordingUrl && <InfoRow label="Recording" value={<a href={b.videoRecordingUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">View Recording</a>} icon={PlayCircle} />}
                  {b.videoRecordingDuration != null && <InfoRow label="Recording Duration" value={`${Math.floor(b.videoRecordingDuration / 60)}m ${b.videoRecordingDuration % 60}s`} icon={Clock} />}
                  {b.transcriptUrl && <InfoRow label="Transcript" value={<a href={b.transcriptUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">View Transcript</a>} icon={FileText} />}
                </div>

                {/* Video session events */}
                {b.videoSessionEvents && b.videoSessionEvents.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/30">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
                      Session Events ({b.videoSessionEvents.length})
                    </p>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {b.videoSessionEvents.map((evt) => (
                        <div key={evt.id} className="flex items-center gap-3 text-xs py-1">
                          <span className="text-muted-foreground/60 w-32 flex-shrink-0">
                            {new Date(evt.eventTime).toLocaleTimeString()}
                          </span>
                          <Badge variant="default" size="sm">{evt.participantRole}</Badge>
                          <span className="text-foreground">{evt.eventType}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Session Monitoring (In-Person) */}
          {b.sessionMonitoring && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                <Mic className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Audio Monitoring</h3>
                <Badge variant={b.sessionMonitoring.status === 'RUNNING' ? 'success' : b.sessionMonitoring.status === 'FAILED' ? 'error' : 'default'} size="sm">{b.sessionMonitoring.status}</Badge>
              </div>
              <div className="p-5">
                {b.sessionMonitoring.livekitRoomName && <InfoRow label="Room" value={b.sessionMonitoring.livekitRoomName} icon={Headphones} mono />}
                {b.sessionMonitoring.startedAt && <InfoRow label="Started" value={formatDateTime(b.sessionMonitoring.startedAt)} icon={Clock} />}
                {b.sessionMonitoring.stoppedAt && <InfoRow label="Stopped" value={formatDateTime(b.sessionMonitoring.stoppedAt)} icon={StopCircle} />}
                {b.sessionMonitoring.errorMessage && <InfoRow label="Error" value={<span className="text-red-400">{b.sessionMonitoring.errorMessage}</span>} icon={AlertCircle} />}
              </div>
            </div>
          )}

          {/* Recording Assets */}
          {b.recordingAssets.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                <PlayCircle className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Recordings ({b.recordingAssets.length})</h3>
              </div>
              <div className="divide-y divide-border/30">
                {b.recordingAssets.map((ra) => (
                  <div key={ra.id} className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant="default" size="sm">{ra.type}</Badge>
                      <a href={ra.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline truncate max-w-xs">{ra.url}</a>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {ra.format && <span>{ra.format}</span>}
                      {ra.durationSeconds != null && <span>{formatHHMMSS(ra.durationSeconds)}</span>}
                      {ra.sizeBytes != null && <span>{formatBytes(ra.sizeBytes)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transcripts */}
          {b.transcripts.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Transcript ({b.transcripts.length} segments)</h3>
              </div>
              <div className="p-5 max-h-80 overflow-y-auto space-y-2">
                {b.transcripts.map((seg) => (
                  <div key={seg.id} className="flex gap-3 text-sm">
                    <span className="text-[10px] text-muted-foreground/60 w-16 flex-shrink-0 pt-0.5 font-mono">
                      {formatMsToTimestamp(seg.tsStartMs)}
                    </span>
                    <Badge variant={seg.speaker === 'provider' ? 'default' : 'info'} size="sm" className="flex-shrink-0 h-5">
                      {seg.speaker}
                    </Badge>
                    <p className="text-foreground">{seg.text}</p>
                    {seg.confidence != null && <span className="text-[10px] text-muted-foreground/40 flex-shrink-0">{(seg.confidence * 100).toFixed(0)}%</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!b.serviceMode && !b.sessionMonitoring && b.videoSessionEvents.length === 0 && b.transcripts.length === 0 && b.recordingAssets.length === 0 && (
            <div className="card p-8 text-center">
              <Video className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No session data available</p>
            </div>
          )}
          </div>
          )}

          {/* ---- REVIEWS TAB ---- */}
          {activeTab === 'reviews' && b && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer's review of provider */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-400" />
                  <h3 className="text-sm font-semibold text-foreground">Customer → Provider</h3>
                </div>
                <div className="p-5">
                  {b.providerRating != null ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={cn('h-5 w-5', i < Math.round(b.providerRating!) ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/20')} />
                        ))}
                        <span className="text-lg font-bold ml-2">{b.providerRating.toFixed(1)}</span>
                      </div>
                      {b.providerReview && <p className="text-sm text-foreground bg-muted/10 p-3 rounded-lg">{b.providerReview}</p>}
                      <div className="flex gap-4 mt-2">
                        {b.providerReviewGoodFit != null && (
                          <div className="flex items-center gap-1.5">
                            {b.providerReviewGoodFit ? <UserCheck className="h-4 w-4 text-emerald-400" /> : <UserX className="h-4 w-4 text-red-400" />}
                            <span className="text-xs text-muted-foreground">Good Fit</span>
                          </div>
                        )}
                        {b.providerReviewWouldBook != null && (
                          <div className="flex items-center gap-1.5">
                            {b.providerReviewWouldBook ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                            <span className="text-xs text-muted-foreground">Would Book Again</span>
                          </div>
                        )}
                        {b.providerReviewWouldRefer != null && (
                          <div className="flex items-center gap-1.5">
                            {b.providerReviewWouldRefer ? <Share2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                            <span className="text-xs text-muted-foreground">Would Refer</span>
                          </div>
                        )}
                      </div>
                      {b.providerRatedAt && <p className="text-[10px] text-muted-foreground/50 mt-2">Rated {formatRelativeTime(b.providerRatedAt)}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground/50 py-4 text-center">No review yet</p>
                  )}
                </div>
              </div>

              {/* Provider's review of customer */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Star className="h-4 w-4 text-purple-400" />
                  <h3 className="text-sm font-semibold text-foreground">Provider → Customer</h3>
                </div>
                <div className="p-5">
                  {b.customerRating != null ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={cn('h-5 w-5', i < Math.round(b.customerRating!) ? 'text-purple-400 fill-purple-400' : 'text-muted-foreground/20')} />
                        ))}
                        <span className="text-lg font-bold ml-2">{b.customerRating.toFixed(1)}</span>
                      </div>
                      {b.customerReview && <p className="text-sm text-foreground bg-muted/10 p-3 rounded-lg">{b.customerReview}</p>}
                      <div className="flex gap-4 mt-2">
                        {b.customerReviewGoodFit != null && (
                          <div className="flex items-center gap-1.5">
                            {b.customerReviewGoodFit ? <UserCheck className="h-4 w-4 text-emerald-400" /> : <UserX className="h-4 w-4 text-red-400" />}
                            <span className="text-xs text-muted-foreground">Good Fit</span>
                          </div>
                        )}
                        {b.customerReviewWouldServe != null && (
                          <div className="flex items-center gap-1.5">
                            {b.customerReviewWouldServe ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                            <span className="text-xs text-muted-foreground">Would Serve Again</span>
                          </div>
                        )}
                        {b.customerReviewWouldRefer != null && (
                          <div className="flex items-center gap-1.5">
                            {b.customerReviewWouldRefer ? <Share2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                            <span className="text-xs text-muted-foreground">Would Refer</span>
                          </div>
                        )}
                      </div>
                      {b.customerRatedAt && <p className="text-[10px] text-muted-foreground/50 mt-2">Rated {formatRelativeTime(b.customerRatedAt)}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground/50 py-4 text-center">No review yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Session Recap */}
            {b.sessionRecap && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Session Recap</h3>
                </div>
                <div className="p-5">
                  <p className="text-sm text-foreground bg-muted/10 p-3 rounded-lg">{b.sessionRecap}</p>
                  {b.recapSubmittedAt && <p className="text-[10px] text-muted-foreground/50 mt-2">Submitted {formatRelativeTime(b.recapSubmittedAt)}</p>}
                </div>
              </div>
            )}
          </div>
          )}

          {/* ---- SAFETY TAB ---- */}
          {activeTab === 'safety' && b && (
          <div className="space-y-6">
            {/* Safety Flags */}
            {b.safetyFlags.length > 0 && (
              <div className="card overflow-hidden border-red-500/20">
                <div className="px-5 py-3 border-b border-red-500/20 bg-red-500/5 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-red-400" />
                  <h3 className="text-sm font-semibold text-red-400">Safety Flags ({b.safetyFlags.length})</h3>
                </div>
                <div className="p-5 space-y-3">
                  {b.safetyFlags.map((flag) => (
                    <div key={flag.id} className="bg-red-500/5 p-4 rounded-lg border border-red-500/10">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={safetyBadgeVariant(flag.severity)} size="sm">{flag.severity}</Badge>
                          <Badge variant="default" size="sm">{flag.category.replace(/_/g, ' ')}</Badge>
                        </div>
                        <span className="text-[10px] text-muted-foreground font-mono">{formatMsToTimestamp(flag.offsetMs)}</span>
                      </div>
                      <p className="text-sm text-foreground">{flag.message}</p>
                      {flag.evidence && (
                        <p className="text-xs text-muted-foreground mt-1 italic bg-muted/10 p-2 rounded">&quot;{flag.evidence}&quot;</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground/60">
                        <span>{formatDateTime(flag.createdAt)}</span>
                        {flag.acknowledged && <Badge variant="success" size="sm">Acknowledged</Badge>}
                        {flag.acknowledgedAt && <span>Ack: {formatDateTime(flag.acknowledgedAt)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User Reports */}
            {b.userReports.length > 0 && (
              <div className="card overflow-hidden border-amber-500/20">
                <div className="px-5 py-3 border-b border-amber-500/20 bg-amber-500/5 flex items-center gap-2">
                  <Ban className="h-4 w-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-400">User Reports ({b.userReports.length})</h3>
                </div>
                <div className="p-5 space-y-3">
                  {b.userReports.map((rpt) => (
                    <div key={rpt.id} className="bg-amber-500/5 p-4 rounded-lg border border-amber-500/10">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant={rpt.status === 'RESOLVED' ? 'success' : rpt.status === 'DISMISSED' ? 'default' : 'warning'} size="sm">{rpt.status}</Badge>
                        <Badge variant="default" size="sm">{rpt.reason.replace(/_/g, ' ')}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <span className="text-muted-foreground">Reporter:</span>
                        <span className="text-foreground">{rpt.reporter.name || rpt.reporter.email || `#${rpt.reporterId}`}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="text-foreground">{rpt.reported.name || rpt.reported.email || `#${rpt.reportedId}`}</span>
                      </div>
                      {rpt.description && <p className="text-sm text-foreground mt-1">{rpt.description}</p>}
                      {rpt.reviewNote && <p className="text-xs text-muted-foreground mt-1 italic">Review: {rpt.reviewNote}</p>}
                      <p className="text-[10px] text-muted-foreground/50 mt-2">{formatDateTime(rpt.createdAt)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No-Show */}
            {b.customerNoShow && (
              <div className="card overflow-hidden border-amber-500/20">
                <div className="px-5 py-3 bg-amber-500/5 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">Customer No-Show</span>
                </div>
              </div>
            )}

            {b.safetyFlags.length === 0 && b.userReports.length === 0 && !b.customerNoShow && (
              <div className="card p-8 text-center">
                <Shield className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No safety concerns</p>
              </div>
            )}
          </div>
          )}

          {/* ---- PAYMENT TAB ---- */}
          {activeTab === 'payment' && b && (
          <div className="space-y-6">
            {/* Payment Overview */}
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Payment Overview</h3>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  <div className="bg-muted/10 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-muted-foreground uppercase">Total</p>
                    <p className="text-lg font-bold text-foreground">{b.isFree ? 'Free' : formatDollars(b.totalAmount)}</p>
                  </div>
                  <div className="bg-muted/10 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-muted-foreground uppercase">Payment Status</p>
                    <Badge variant={paymentStatusVariant(b.paymentStatus)} size="sm" className="mt-1">{b.paymentStatus.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="bg-muted/10 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-muted-foreground uppercase">Required</p>
                    <p className="text-sm font-medium text-foreground mt-1">{b.paymentRequired ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="bg-muted/10 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-muted-foreground uppercase">Free</p>
                    <p className="text-sm font-medium text-foreground mt-1">{b.isFree ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                {b.paymentIntentId && <InfoRow label="Payment Intent" value={b.paymentIntentId} icon={Hash} mono />}
                {b.checkoutIdempotencyKey && <InfoRow label="Checkout Key" value={b.checkoutIdempotencyKey} icon={Hash} mono />}
              </div>
            </div>

            {/* Pricing Snapshot */}
            {b.pricingSnapshot && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Pricing Breakdown</h3>
                </div>
                <div className="p-5 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">{formatCents(b.pricingSnapshot.subtotalCents)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Platform Fee</span><span className="font-medium">{formatCents(b.pricingSnapshot.platformFeeCents)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Stripe Fee</span><span className="font-medium">{formatCents(b.pricingSnapshot.stripeFeeCents)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Taxes</span><span className="font-medium">{formatCents(b.pricingSnapshot.taxesCents)}</span></div>
                  {b.pricingSnapshot.creditDiscountCents != null && Number(b.pricingSnapshot.creditDiscountCents) > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-emerald-400">Credit Discount</span><span className="font-medium text-emerald-400">-{formatCents(Number(b.pricingSnapshot.creditDiscountCents))}</span></div>
                  )}
                  <div className="flex justify-between text-sm font-bold border-t border-border/30 pt-2 mt-2"><span>Total</span><span>{formatCents(b.pricingSnapshot.totalCents)}</span></div>
                  <div className="flex justify-between text-sm text-muted-foreground"><span>Provider Payout</span><span>{formatCents(b.pricingSnapshot.providerPayoutCents)}</span></div>
                </div>
              </div>
            )}

            {/* Payment Records */}
            {b.payments.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Payments ({b.payments.length})</h3>
                </div>
                <div className="p-5 space-y-3">
                  {b.payments.map((p) => (
                    <div key={p.id} className="bg-muted/10 p-4 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant={paymentStatusVariant(p.status)} size="sm">{p.status}</Badge>
                        <span className="text-sm font-bold">{formatDollars(p.amount)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Method: </span><span>{p.paymentMethod?.replace(/_/g, ' ') || 'N/A'}</span></div>
                        {p.cardBrand && <div><span className="text-muted-foreground">Card: </span><span>{p.cardBrand} ****{p.cardLast4}</span></div>}
                        {p.processingFee != null && <div><span className="text-muted-foreground">Processing Fee: </span><span>{formatDollars(p.processingFee)}</span></div>}
                        {p.platformFee != null && <div><span className="text-muted-foreground">Platform Fee: </span><span>{formatDollars(p.platformFee)}</span></div>}
                      </div>
                      {p.refundAmount != null && p.refundAmount > 0 && (
                        <div className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Refunded: {formatDollars(p.refundAmount)} {p.refundReason && `(${p.refundReason})`}</div>
                      )}
                      {p.failureReason && (
                        <div className="text-xs text-red-400 flex items-center gap-1"><XCircle className="h-3 w-3" /> {p.failureReason}</div>
                      )}
                      <div className="flex gap-4 text-[10px] text-muted-foreground/60 flex-wrap">
                        {p.stripeChargeId && <span className="font-mono">{p.stripeChargeId}</span>}
                        {p.paidAt && <span>Paid: {formatDateTime(p.paidAt)}</span>}
                        <span>Created: {formatDateTime(p.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payouts */}
            {b.payouts.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-emerald-400" />
                  <h3 className="text-sm font-semibold text-foreground">Payouts ({b.payouts.length})</h3>
                </div>
                <div className="p-5 space-y-3">
                  {b.payouts.map((po) => (
                    <div key={po.id} className="bg-muted/10 p-4 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant={payoutStatusVariant(po.status)} size="sm">{po.status}</Badge>
                          <Badge variant="default" size="sm">{po.type.replace(/_/g, ' ')}</Badge>
                        </div>
                        <span className="text-sm font-bold">{formatDollars(po.netAmount)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Gross: </span><span>{formatDollars(po.amount)}</span></div>
                        <div><span className="text-muted-foreground">Platform Fee: </span><span>{formatDollars(po.platformFee)}</span></div>
                        <div><span className="text-muted-foreground">Net: </span><span className="font-medium">{formatDollars(po.netAmount)}</span></div>
                        {po.paidAt && <div><span className="text-muted-foreground">Paid: </span><span>{formatDateTime(po.paidAt)}</span></div>}
                      </div>
                      {po.failureMessage && (
                        <div className="text-xs text-red-400 flex items-center gap-1"><XCircle className="h-3 w-3" /> {po.failureCode}: {po.failureMessage}</div>
                      )}
                      <div className="flex gap-4 text-[10px] text-muted-foreground/60 flex-wrap">
                        {po.stripeTransferId && <span className="font-mono">{po.stripeTransferId}</span>}
                        <span>Requested: {formatDateTime(po.requestedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ledger Entries */}
            {b.ledgerEntries.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <BookMarked className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Ledger ({b.ledgerEntries.length})</h3>
                </div>
                <div className="divide-y divide-border/30">
                  {b.ledgerEntries.map((le) => (
                    <div key={le.id} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant={le.status === 'RECONCILED' ? 'success' : le.status === 'FAILED' ? 'error' : 'warning'} size="sm">{le.status}</Badge>
                          <span className="text-xs font-medium">{le.entryType.replace(/_/g, ' ')}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{le.description}</p>
                      </div>
                      <div className="text-right">
                        <p className={cn('text-sm font-bold', le.amount >= 0 ? 'text-emerald-400' : 'text-red-400')}>{formatDollars(le.amount)}</p>
                        <p className="text-[10px] text-muted-foreground">{formatDateTime(le.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Credit Usages */}
            {b.creditUsages.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Gift className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Credit Usages ({b.creditUsages.length})</h3>
                </div>
                <div className="divide-y divide-border/30">
                  {b.creditUsages.map((cu) => (
                    <div key={cu.id} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-medium">{cu.credit.sourceType}</span>
                        <p className="text-[10px] text-muted-foreground">Credit #{cu.creditId} &middot; {cu.credit.status}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-emerald-400">-{formatCents(cu.amountCents)}</p>
                        <p className="text-[10px] text-muted-foreground">{formatDateTime(cu.usedAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Operations */}
            {b.paymentOperations.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Payment Operations ({b.paymentOperations.length})</h3>
                </div>
                <div className="divide-y divide-border/30">
                  {b.paymentOperations.map((op) => (
                    <div key={op.id} className="px-5 py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant={op.status === 'COMPLETED' ? 'success' : op.status === 'FAILED' ? 'error' : 'warning'} size="sm">{op.status}</Badge>
                          <span className="text-xs font-medium">{op.operationType}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground">Attempts: {op.attemptCount}</span>
                      </div>
                      {op.errorMessage && <p className="text-xs text-red-400 mt-1">{op.errorMessage}</p>}
                      <p className="text-[10px] text-muted-foreground/60 font-mono mt-1">{op.idempotencyKey}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* ---- TIMELINE TAB ---- */}
          {activeTab === 'timeline' && (
          <div className="space-y-4">
            {timeline.length > 0 ? (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Event Timeline ({timeline.length})</h3>
                </div>
                <div className="p-5">
                  <div className="relative">
                    <div className="absolute left-3 top-0 bottom-0 w-px bg-border/50" />
                    <div className="space-y-4">
                      {timeline.map((evt, i) => (
                        <div key={i} className="flex items-start gap-4 relative">
                          <div className={cn('w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10', severityColor(evt.severity || 'info'))}>
                            <div className="w-2 h-2 rounded-full bg-white" />
                          </div>
                          <div className="flex-1 min-w-0 pb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-foreground">{evt.label}</span>
                              <Badge variant="default" size="sm">{evt.type}</Badge>
                            </div>
                            {evt.details && <p className="text-xs text-muted-foreground mt-0.5">{evt.details}</p>}
                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">{formatDateTime(evt.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card p-8 text-center">
                <Activity className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No timeline events</p>
              </div>
            )}
          </div>
          )}

          {/* ---- SUPPORT TAB ---- */}
          {activeTab === 'support' && b && (
          <div className="space-y-6">
            {b.supportTickets.length > 0 ? (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <LifeBuoy className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Support Tickets ({b.supportTickets.length})</h3>
                </div>
                <div className="p-5 space-y-3">
                  {b.supportTickets.map((t) => (
                    <div key={t.id} className="bg-muted/10 p-4 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant={t.status === 'RESOLVED' || t.status === 'CLOSED' ? 'success' : t.status === 'IN_PROGRESS' ? 'info' : 'warning'} size="sm">{t.status}</Badge>
                          <Badge variant={t.priority === 'URGENT' ? 'error' : t.priority === 'HIGH' ? 'warning' : 'default'} size="sm">{t.priority}</Badge>
                        </div>
                        <Badge variant="default" size="sm">{t.category}</Badge>
                      </div>
                      <p className="text-sm font-medium text-foreground">{t.title}</p>
                      {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                      <div className="flex gap-4 text-[10px] text-muted-foreground/60 flex-wrap">
                        <span>Source: {t.source}</span>
                        <span>Messages: {t._count.messages}</span>
                        <span>Calls: {t._count.calls}</span>
                        <span>Created: {formatDateTime(t.createdAt)}</span>
                        {t.resolvedAt && <span>Resolved: {formatDateTime(t.resolvedAt)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="card p-8 text-center">
                <LifeBuoy className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No support tickets</p>
              </div>
            )}
          </div>
          )}

          {/* ---- REFERRALS TAB ---- */}
          {activeTab === 'referrals' && b && (
          <div className="space-y-6">
            {/* Qualified Referral */}
            {b.qualifiedReferral && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <Gift className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Qualified Referral</h3>
                </div>
                <div className="p-5">
                  <InfoRow label="Status" value={<Badge variant={b.qualifiedReferral.status === 'CREDITED' ? 'success' : b.qualifiedReferral.status === 'INVALIDATED' ? 'error' : 'warning'} size="sm">{b.qualifiedReferral.status}</Badge>} icon={Info} />
                  <InfoRow label="Referral Code" value={b.qualifiedReferral.referralCode} icon={Hash} mono />
                  <InfoRow label="Referrer" value={b.qualifiedReferral.referrer.name || b.qualifiedReferral.referrer.email || `#${b.qualifiedReferral.referrerUserId}`} icon={User} />
                  <InfoRow label="Referee" value={b.qualifiedReferral.referee.name || b.qualifiedReferral.referee.email || `#${b.qualifiedReferral.refereeUserId}`} icon={User} />
                  {b.qualifiedReferral.referrerRewardCents != null && <InfoRow label="Referrer Reward" value={formatCents(b.qualifiedReferral.referrerRewardCents)} icon={DollarSign} />}
                  {b.qualifiedReferral.refereeRewardCents != null && <InfoRow label="Referee Reward" value={formatCents(b.qualifiedReferral.refereeRewardCents)} icon={DollarSign} />}
                  {b.qualifiedReferral.qualifiedAt && <InfoRow label="Qualified At" value={formatDateTime(b.qualifiedReferral.qualifiedAt)} icon={Clock} />}
                </div>
              </div>
            )}

            {/* Credits Used */}
            {b.creditsUsed.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-emerald-400" />
                  <h3 className="text-sm font-semibold text-foreground">Credits Used ({b.creditsUsed.length})</h3>
                </div>
                <div className="divide-y divide-border/30">
                  {b.creditsUsed.map((cr) => (
                    <div key={cr.id} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant={cr.status === 'FULLY_USED' ? 'success' : cr.status === 'REVOKED' ? 'error' : 'warning'} size="sm">{cr.status}</Badge>
                          <span className="text-xs">{cr.sourceType}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{cr.user.name || cr.user.email || `User #${cr.userId}`}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">{formatCents(cr.amountCents)}</p>
                        <p className="text-[10px] text-muted-foreground">Remaining: {formatCents(cr.remainingCents)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!b.qualifiedReferral && b.creditsUsed.length === 0 && b.creditUsages.length === 0 && (
              <div className="card p-8 text-center">
                <Gift className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No referrals or credits</p>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Content Row (expandable)
// ============================================================================

const ContentRow: React.FC<{
  content: UserContentItem;
  expanded: boolean;
  onToggle: () => void;
  onRegenerate: (assetId: string) => void;
  regenerating: boolean;
}> = ({ content, expanded, onToggle, onRegenerate, regenerating }) => {
  const showRegenerateButton = (content.source === 'VEO' || content.source === 'AI_GENERATED') && content.serviceTag !== null;
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
  <>
  <div
    className={cn(
      'border-b border-border/30 last:border-0 transition-all cursor-pointer',
      expanded ? 'bg-muted/10 ring-1 ring-primary/20' : 'hover:bg-muted/10'
    )}
    onClick={onToggle}
  >
    <div className="flex items-center gap-4 px-4 py-3">
      {/* Expand indicator */}
      <div className="flex-shrink-0">
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </div>

      {/* Thumbnail */}
      <div className="w-12 h-12 rounded-lg bg-muted/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {content.posterPath || content.externalPosterUrl ? (
          <img
            src={content.externalPosterUrl || content.posterPath || ''}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : content.type === 'VIDEO' ? (
          <Video className="h-5 w-5 text-muted-foreground/50" />
        ) : (
          <Image className="h-5 w-5 text-muted-foreground/50" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="default" size="sm">{content.type}</Badge>
          <Badge variant="default" size="sm">{content.source}</Badge>
          {content.serviceTag ? (
            <Badge variant="success" size="sm">
              <Tag className="h-2.5 w-2.5 mr-0.5" />
              {content.serviceTag}
            </Badge>
          ) : (
            <Badge variant="muted" size="sm">Untagged</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1 truncate">
          {content.caption || 'No caption'}
        </p>
      </div>

      {/* Status */}
      <Badge variant={getContentStatusVariant(content.status)} size="sm">
        {content.status.replace(/_/g, ' ')}
      </Badge>

      {/* Engagement */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-1">
          <Eye className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs text-foreground">{formatCompactNumber(content.viewCount)}</span>
        </div>
        <div className="flex items-center gap-1">
          <ThumbsUp className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs text-foreground">{formatCompactNumber(content.likesCount)}</span>
        </div>
      </div>

      {/* Duration */}
      <div className="flex-shrink-0 w-14 text-right">
        {content.durationSec != null ? (
          <span className="text-xs text-muted-foreground">
            {Math.floor(content.durationSec / 60)}:{(content.durationSec % 60).toString().padStart(2, '0')}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground">--</span>
        )}
      </div>

      {/* Date */}
      <div className="flex-shrink-0 w-24 text-right">
        <p className="text-xs text-muted-foreground">
          {formatRelativeTime(content.createdAt)}
        </p>
      </div>
    </div>

    {/* Expanded detail panel */}
    {expanded && (
      <div className="px-4 pb-4 pt-1 border-t border-border/30" onClick={(e) => e.stopPropagation()}>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4">
          {/* Media preview — video player or image */}
          <div className="w-full md:w-72">
            {content.type === 'VIDEO' && content.videoUrl ? (
              <div className="relative rounded-lg overflow-hidden bg-black border border-border/50">
                <video
                  key={content.videoUrl}
                  src={content.videoUrl}
                  poster={content.posterUrl || content.externalPosterUrl || content.posterPath || undefined}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-auto block max-h-[320px]"
                />
              </div>
            ) : (content.posterUrl || content.posterPath || content.externalPosterUrl) ? (
              <div className="rounded-lg overflow-hidden bg-muted/20 border border-border/50">
                <img
                  src={content.posterUrl || content.externalPosterUrl || content.posterPath || ''}
                  alt={content.caption || 'Content preview'}
                  className="w-full h-auto object-contain max-h-[320px]"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-48 rounded-lg bg-muted/20 flex items-center justify-center">
                {content.type === 'VIDEO' ? (
                  <Video className="h-12 w-12 text-muted-foreground/30" />
                ) : (
                  <Image className="h-12 w-12 text-muted-foreground/30" />
                )}
              </div>
            )}
          </div>

          {/* Detail fields */}
          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Content Info</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">ID</span>
                  <span className="text-[10px] text-foreground font-mono break-all">{content.id}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Type</span>
                  <span className="text-[10px] text-foreground">{content.type}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Source</span>
                  <span className="text-[10px] text-foreground">{content.source}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Status</span>
                  <Badge variant={getContentStatusVariant(content.status)} size="sm">{content.status.replace(/_/g, ' ')}</Badge>
                </div>
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Service Tag</span>
                  <span className="text-[10px] text-foreground">
                    {content.serviceTag ? (
                      <Badge variant="success" size="sm"><Tag className="h-2.5 w-2.5 mr-0.5" />{content.serviceTag}</Badge>
                    ) : (
                      <span className="text-muted-foreground italic">None</span>
                    )}
                  </span>
                </div>
                {content.durationSec != null && (
                  <div className="flex items-baseline justify-between gap-2 py-0.5">
                    <span className="text-[10px] text-muted-foreground">Duration</span>
                    <span className="text-[10px] text-foreground">
                      {Math.floor(content.durationSec / 60)}:{(content.durationSec % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {content.caption && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Caption</p>
                <p className="text-xs text-foreground bg-muted/10 rounded-lg p-2">{content.caption}</p>
              </div>
            )}

            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Engagement</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-2 bg-muted/10 rounded-lg">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <Eye className="h-3 w-3 text-blue-400" />
                    <span className="text-sm font-bold text-foreground">{formatCompactNumber(content.viewCount)}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Views</p>
                </div>
                <div className="text-center p-2 bg-muted/10 rounded-lg">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <ThumbsUp className="h-3 w-3 text-red-400" />
                    <span className="text-sm font-bold text-foreground">{formatCompactNumber(content.likesCount)}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Likes</p>
                </div>
              </div>
            </div>

            {/* Regenerate Video button */}
            {showRegenerateButton && (
              <div>
                <button
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={regenerating}
                  onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
                >
                  {regenerating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  {regenerating ? 'Regenerating...' : 'Regenerate Video'}
                </button>
              </div>
            )}

            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Dates</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Created</span>
                  <span className="text-[10px] text-foreground">{formatDateTime(content.createdAt)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Updated</span>
                  <span className="text-[10px] text-foreground">{formatDateTime(content.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>

  {/* Regenerate Confirmation Dialog */}
  {confirmOpen && (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmOpen(false)}>
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <RefreshCw className="h-5 w-5 text-amber-400" />
          </div>
          <h3 className="text-sm font-semibold text-foreground">Regenerate video for &ldquo;{content.serviceTag}&rdquo;?</h3>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          This will mark the current video as rejected and queue a new AI-generated video. The new video typically takes 2–5 minutes to appear.
        </p>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/20 text-muted-foreground hover:bg-muted/30 transition-colors"
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
            onClick={() => {
              setConfirmOpen(false);
              onRegenerate(content.id);
            }}
          >
            Regenerate
          </button>
        </div>
      </div>
    </div>
  )}
  </>
  );
};

// ============================================================================
// Service Card (expanded detail for Services tab)
// ============================================================================

const ServiceCard: React.FC<{ svc: UserDetailService }> = ({ svc }) => {
  const [expanded, setExpanded] = useState(false);

  const formatServiceMode = (mode: string) =>
    mode === 'IN_PERSON' ? 'In Person' : mode === 'VIRTUAL' ? 'Virtual' : mode;

  const priceDisplay = svc.isFree
    ? 'Free'
    : svc.pricePerHour != null
      ? `$${Number(svc.pricePerHour).toFixed(2)}/hr`
      : svc.priceCents != null
        ? formatCentsDecimal(svc.priceCents)
        : '—';

  return (
    <div
      className={cn(
        'border border-border rounded-lg overflow-hidden transition-all cursor-pointer hover:bg-muted/10',
        expanded && 'ring-1 ring-primary/30'
      )}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Summary row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {expanded
              ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
              : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{svc.serviceName}</p>
            <p className="text-[10px] text-muted-foreground">
              ID: {svc.id}
              {svc.serviceCategory && <> &middot; {svc.serviceCategory.categoryName}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          {/* Price */}
          <span className="text-xs font-medium text-foreground">{priceDisplay}</span>
          {/* Service modes */}
          {svc.serviceModeOptions && svc.serviceModeOptions.length > 0 && svc.serviceModeOptions.map((mode) => (
            <Badge key={mode} variant={mode === 'VIRTUAL' ? 'info' : 'muted'} size="sm">
              {formatServiceMode(mode)}
            </Badge>
          ))}
          {/* Status */}
          {svc.status ? (
            <Badge variant={svc.status === 'ACTIVE' ? 'success' : svc.status === 'DRAFT' ? 'warning' : 'muted'} size="sm">
              {svc.status}
            </Badge>
          ) : svc.isActive ? (
            <Badge variant="success" size="sm">Active</Badge>
          ) : (
            <Badge variant="muted" size="sm">Inactive</Badge>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-3 bg-muted/5" onClick={(e) => e.stopPropagation()}>
          {/* Pricing & Duration */}
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Pricing & Duration</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <div className="flex items-baseline justify-between gap-2 py-0.5">
                <span className="text-[10px] text-muted-foreground">Price / Hour</span>
                <span className="text-[10px] text-foreground font-medium">
                  {svc.pricePerHour != null ? `$${Number(svc.pricePerHour).toFixed(2)}` : '—'}
                </span>
              </div>
              {svc.priceCents != null && (
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Price (cents)</span>
                  <span className="text-[10px] text-foreground font-mono">{formatCentsDecimal(svc.priceCents)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between gap-2 py-0.5">
                <span className="text-[10px] text-muted-foreground">Free</span>
                <span className="text-[10px] text-foreground">{svc.isFree ? 'Yes' : 'No'}</span>
              </div>
              {svc.duration != null && (
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Duration</span>
                  <span className="text-[10px] text-foreground">{svc.duration} min</span>
                </div>
              )}
            </div>
          </div>

          {/* Service Details */}
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Service Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              {svc.serviceCategory && (
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Category</span>
                  <span className="text-[10px] text-foreground">{svc.serviceCategory.categoryName}</span>
                </div>
              )}
              {svc.serviceModeOptions && svc.serviceModeOptions.length > 0 && (
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Service Modes</span>
                  <span className="text-[10px] text-foreground">
                    {svc.serviceModeOptions.map(formatServiceMode).join(', ')}
                  </span>
                </div>
              )}
              {svc.maxComboSize != null && (
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Max Group Size</span>
                  <span className="text-[10px] text-foreground">
                    {svc.maxComboSize === 1 ? '1 (Individual)' : `${svc.maxComboSize} people`}
                  </span>
                </div>
              )}
              {(svc.ageMin != null || svc.ageMax != null) && (
                <div className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Age Range</span>
                  <span className="text-[10px] text-foreground">
                    {svc.ageMin ?? 0}–{svc.ageMax ?? '∞'}
                  </span>
                </div>
              )}
              <div className="flex items-baseline justify-between gap-2 py-0.5">
                <span className="text-[10px] text-muted-foreground">Status</span>
                <span className="text-[10px] text-foreground">
                  {svc.status || (svc.isActive ? 'Active' : 'Inactive')}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          {svc.description && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Description</p>
              <p className="text-[11px] text-foreground leading-relaxed bg-muted/10 rounded-lg p-3 whitespace-pre-wrap">
                {svc.description}
              </p>
            </div>
          )}

          {/* Timestamps */}
          {(svc.createdAt || svc.updatedAt) && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Timeline</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                {svc.createdAt && (
                  <div className="flex items-baseline justify-between gap-2 py-0.5">
                    <span className="text-[10px] text-muted-foreground">Created</span>
                    <span className="text-[10px] text-foreground">{formatDateTime(svc.createdAt)}</span>
                  </div>
                )}
                {svc.updatedAt && (
                  <div className="flex items-baseline justify-between gap-2 py-0.5">
                    <span className="text-[10px] text-muted-foreground">Updated</span>
                    <span className="text-[10px] text-foreground">{formatDateTime(svc.updatedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT: UserFullView
// ============================================================================

const UserFullView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = id ? parseInt(id, 10) : null;

  // ---- State ----
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [bookingRole, setBookingRole] = useState<'all' | 'customer' | 'provider'>('all');
  const [bookingStatus, setBookingStatus] = useState<string>('');
  const [contentStatus, setContentStatus] = useState<string>('');
  const [expandedContentId, setExpandedContentId] = useState<string | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);

  // ---- Data fetching ----
  const { user, loading: userLoading, error: userError, refetch: refetchUser } = useUserDetail(userId);

  const bookingsParams = useMemo(
    () => ({ role: bookingRole, status: bookingStatus || undefined, limit: 20 }),
    [bookingRole, bookingStatus]
  );
  const {
    items: bookings,
    total: bookingsTotal,
    loading: bookingsLoading,
    error: bookingsError,
    hasMore: bookingsHasMore,
    refetch: refetchBookings,
    loadMore: loadMoreBookings,
  } = useUserBookings(userId, bookingsParams);

  const contentParams = useMemo(
    () => ({ status: contentStatus || undefined, limit: 20 }),
    [contentStatus]
  );
  const {
    items: contentItems,
    total: contentTotal,
    loading: contentLoading,
    error: contentError,
    hasMore: contentHasMore,
    refetch: refetchContent,
    loadMore: loadMoreContent,
  } = useUserContent(userId, contentParams);

  const handleRegenerate = useCallback(async (assetId: string) => {
    setRegeneratingId(assetId);
    try {
      const res = await regenerateVideo(assetId);
      showToast('success', `Video regeneration queued for "${contentItems.find(c => c.id === assetId)?.serviceTag || 'service'}"`);
      // Refetch to show updated status (REJECTED on old asset)
      await refetchContent();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to regenerate video';
      showToast('error', message);
    } finally {
      setRegeneratingId(null);
    }
  }, [contentItems, refetchContent]);

  // Only fetch full detail when a booking is actually selected
  const [bookingDetail, setBookingDetail] = useState<BookingDetailType | null>(null);
  const [bookingDetailLoading, setBookingDetailLoading] = useState(false);
  const [bookingDetailError, setBookingDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedBookingId) {
      setBookingDetail(null);
      setBookingDetailLoading(false);
      setBookingDetailError(null);
      return;
    }

    let cancelled = false;
    setBookingDetailLoading(true);
    setBookingDetailError(null);

    import('../services/users').then(({ getBookingDetail }) =>
      getBookingDetail(selectedBookingId)
    ).then((res) => {
      if (!cancelled) {
        setBookingDetail(res.data);
        setBookingDetailLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        setBookingDetailError(err instanceof Error ? err.message : 'Failed to fetch booking details');
        setBookingDetailLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [selectedBookingId]);

  // Find the selected booking from the list for fallback data
  const selectedBookingListItem = useMemo(
    () => selectedBookingId ? bookings.find((b) => b.id === selectedBookingId) || null : null,
    [bookings, selectedBookingId]
  );

  // ---- Handlers ----
  const handleGoBack = useCallback(() => {
    navigate('/users');
  }, [navigate]);

  const handleBookingClick = useCallback((bookingId: number) => {
    setSelectedBookingId(bookingId);
  }, []);

  const handleCloseBookingDetail = useCallback(() => {
    setSelectedBookingId(null);
  }, []);

  const handleViewUserFromBooking = useCallback((uid: number) => {
    navigate(`/users/${uid}`);
  }, [navigate]);

  // ---- Loading / Error states ----
  if (userLoading && !user) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-sm">
          <XCircle className="h-12 w-12 text-destructive/30 mx-auto mb-3" />
          <p className="text-sm text-destructive">{userError || 'User not found'}</p>
          <button
            onClick={handleGoBack}
            className="mt-4 px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  const metrics = user.metrics;

  // ---- Computed ----
  const completionRate =
    metrics.bookingsAsCustomerCount + metrics.bookingsAsProviderCount > 0
      ? (
          ((metrics.completedBookingsAsCustomer + metrics.completedBookingsAsProvider) /
            (metrics.bookingsAsCustomerCount + metrics.bookingsAsProviderCount)) *
          100
        ).toFixed(1)
      : '0';

  // ---- Render ----
  return (
    <div className="h-full flex flex-col">
      {/* ================================================================== */}
      {/* HEADER BAR */}
      {/* ================================================================== */}
      <div className="flex-shrink-0 border-b border-border bg-card px-6 py-4">
        <div className="flex items-center gap-4">
          {/* Back button */}
          <button
            onClick={handleGoBack}
            className="p-2 rounded-lg hover:bg-muted/30 transition-colors"
            title="Back to Users"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </button>

          {/* User summary */}
          <Avatar
            src={user.profileImage || undefined}
            name={user.name || 'User'}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-foreground truncate">
                {user.name || 'Unnamed User'}
              </h1>
              <span className="text-sm text-muted-foreground">#{user.id}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              {user.isActive ? (
                <Badge variant="success" size="sm">Active</Badge>
              ) : (
                <Badge variant="error" size="sm">Inactive</Badge>
              )}
              {user.isVerified && (
                <Badge variant="info" size="sm">Verified</Badge>
              )}
              {user.isProvider && (
                <Badge variant="default" size="sm">Provider</Badge>
              )}
              {user.chargesEnabled && (
                <Badge variant="success" size="sm">Stripe</Badge>
              )}
              {user.rating != null && user.rating > 0 && (
                <div className="flex items-center gap-1 ml-2">
                  <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm font-medium text-foreground">
                    {user.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({formatNumber(user.totalReviews)} reviews)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowNotifyModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border hover:bg-muted/30 transition-colors text-sm text-muted-foreground hover:text-foreground"
              title="Send notification to this user"
            >
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Notify</span>
            </button>
            <button
              onClick={() => refetchUser()}
              className="p-2 rounded-lg border border-border hover:bg-muted/30 transition-colors"
              title="Refresh user data"
            >
              <RefreshCw className={cn('h-4 w-4 text-muted-foreground', userLoading && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mt-4 overflow-x-auto scrollbar-none">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                activeTab === tab.key
                  ? 'bg-primary/15 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.key === 'bookings' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/30">
                  {formatCompactNumber(metrics.bookingsAsCustomerCount + metrics.bookingsAsProviderCount)}
                </span>
              )}
              {tab.key === 'content' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/30">
                  {formatCompactNumber(metrics.mediaAssetsCount)}
                </span>
              )}
              {tab.key === 'services' && user.services.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/30">
                  {user.services.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ================================================================== */}
      {/* TAB CONTENT */}
      {/* ================================================================== */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="p-6 space-y-5 max-w-7xl mx-auto">

          {/* ============================================================ */}
          {/* OVERVIEW TAB */}
          {/* ============================================================ */}
          {activeTab === 'overview' && (
            <>
              {/* KPI metrics row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <MetricCard
                  label="Bookings (Customer)"
                  value={formatNumber(metrics.bookingsAsCustomerCount)}
                  icon={Calendar}
                  color="text-blue-400"
                  bgColor="bg-blue-400/10"
                  subtitle={`${formatNumber(metrics.completedBookingsAsCustomer)} completed`}
                />
                <MetricCard
                  label="Bookings (Provider)"
                  value={formatNumber(metrics.bookingsAsProviderCount)}
                  icon={Briefcase}
                  color="text-emerald-400"
                  bgColor="bg-emerald-400/10"
                  subtitle={`${formatNumber(metrics.completedBookingsAsProvider)} completed`}
                />
                <MetricCard
                  label="Total Spent"
                  value={formatCents(metrics.totalSpentCents)}
                  icon={CreditCard}
                  color="text-orange-400"
                  bgColor="bg-orange-400/10"
                />
                <MetricCard
                  label="Total Earned"
                  value={formatCents(metrics.totalEarnedCents)}
                  icon={DollarSign}
                  color="text-emerald-400"
                  bgColor="bg-emerald-400/10"
                />
                <MetricCard
                  label="Media Assets"
                  value={formatNumber(metrics.mediaAssetsCount)}
                  icon={Film}
                  color="text-primary"
                  bgColor="bg-primary/10"
                  subtitle={`${formatCompactNumber(metrics.contentViewsTotal)} views`}
                />
                <MetricCard
                  label="Referrals Made"
                  value={formatNumber(metrics.referralsMade)}
                  icon={Share2}
                  color="text-purple-400"
                  bgColor="bg-purple-400/10"
                />
              </div>

              {/* Two-column layout for profile + social */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Profile Information */}
                <SectionCard title="Profile Information" icon={User}>
                  <div className="space-y-0">
                    <InfoRow label="Full Name" value={user.name} icon={User} />
                    <InfoRow label="Email" value={user.email} icon={Mail} />
                    <InfoRow
                      label="Phone"
                      value={user.phone ? formatPhoneNumber(user.phone) : null}
                      icon={Phone}
                    />
                    <InfoRow label="Bio" value={user.bio} icon={FileText} />
                    <InfoRow label="Gender" value={user.gender} icon={User} />
                    <InfoRow label="Pronouns" value={user.pronouns} />
                    <InfoRow
                      label="Date of Birth"
                      value={
                        user.dateOfBirth
                          ? parseDateOnly(user.dateOfBirth).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })
                          : null
                      }
                      icon={Calendar}
                    />
                    <InfoRow label="Referral Code" value={user.referralCode} icon={Hash} mono />
                  </div>
                </SectionCard>

                {/* Ratings & Social */}
                <SectionCard title="Ratings & Social" icon={Star}>
                  <div className="space-y-0">
                    <InfoRow
                      label="Overall Rating"
                      value={
                        user.rating != null ? (
                          <div className="flex items-center gap-1.5">
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                            <span className="font-semibold">{user.rating.toFixed(2)}</span>
                            <span className="text-muted-foreground text-xs">
                              ({formatNumber(user.totalReviews)} reviews)
                            </span>
                          </div>
                        ) : 'No rating'
                      }
                      icon={Star}
                    />
                    <InfoRow
                      label="Customer Rating"
                      value={
                        user.customerRating != null
                          ? `${user.customerRating.toFixed(2)} / 5.0`
                          : null
                      }
                    />
                    <InfoRow
                      label="Provider Rating"
                      value={
                        user.providerRating != null
                          ? `${user.providerRating.toFixed(2)} / 5.0`
                          : null
                      }
                    />
                    <InfoRow
                      label="Followers"
                      value={formatNumber(user.followerCount)}
                      icon={Heart}
                    />
                    <InfoRow
                      label="Connections"
                      value={formatNumber(user.connectionCount)}
                      icon={UsersIcon}
                    />
                    <InfoRow
                      label="Content Likes (Total)"
                      value={formatNumber(metrics.contentLikesTotal)}
                      icon={ThumbsUp}
                    />
                    <InfoRow
                      label="Content Views (Total)"
                      value={formatNumber(metrics.contentViewsTotal)}
                      icon={Eye}
                    />
                  </div>
                </SectionCard>
              </div>

              {/* Activity & Group Sessions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Activity & Timestamps */}
                <SectionCard title="Activity & Timestamps" icon={Clock}>
                  <div className="space-y-0">
                    <InfoRow
                      label="Account Created"
                      value={formatDateTime(user.createdAt)}
                      icon={Calendar}
                    />
                    <InfoRow
                      label="Last Updated"
                      value={formatDateTime(user.updatedAt)}
                      icon={Clock}
                    />
                    <InfoRow
                      label="Last Seen"
                      value={user.lastSeenAt ? formatDateTime(user.lastSeenAt) : 'Never'}
                      icon={Eye}
                    />
                    <InfoRow
                      label="Is Active"
                      value={
                        <span className={user.isActive ? 'text-emerald-400' : 'text-red-400'}>
                          {user.isActive ? 'Yes' : 'No'}
                        </span>
                      }
                      icon={Activity}
                    />
                    <InfoRow
                      label="Is Verified"
                      value={
                        <span className={user.isVerified ? 'text-emerald-400' : 'text-muted-foreground'}>
                          {user.isVerified ? 'Yes' : 'No'}
                        </span>
                      }
                      icon={Shield}
                    />
                    <InfoRow
                      label="Is Provider"
                      value={user.isProvider ? 'Yes' : 'No'}
                      icon={Briefcase}
                    />
                  </div>
                </SectionCard>

                {/* Group Sessions & Support */}
                <SectionCard title="Group Sessions & Support" icon={UsersIcon}>
                  <div className="space-y-0">
                    <InfoRow
                      label="Group Sessions Hosted"
                      value={formatNumber(metrics.groupSessionsHosted)}
                      icon={Video}
                    />
                    <InfoRow
                      label="Group Sessions Attended"
                      value={formatNumber(metrics.groupSessionsAttended)}
                      icon={UsersIcon}
                    />
                    <InfoRow
                      label="Support Tickets"
                      value={formatNumber(metrics.supportTicketsCount)}
                      icon={FileText}
                    />
                    <InfoRow
                      label="Referrals Made"
                      value={formatNumber(metrics.referralsMade)}
                      icon={Share2}
                    />
                    <InfoRow
                      label="Completion Rate"
                      value={`${completionRate}%`}
                      icon={CheckCircle}
                    />
                  </div>
                </SectionCard>
              </div>

              {/* Stripe / Payment info */}
              <SectionCard title="Stripe & Payment" icon={CreditCard}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
                  <InfoRow
                    label="Stripe Customer ID"
                    value={user.stripeCustomerId}
                    icon={CreditCard}
                    mono
                  />
                  <InfoRow
                    label="Stripe Account ID"
                    value={user.stripeAccountId}
                    icon={Link2}
                    mono
                  />
                  <InfoRow
                    label="Charges Enabled"
                    value={
                      <span className={user.chargesEnabled ? 'text-emerald-400' : 'text-muted-foreground'}>
                        {user.chargesEnabled ? 'Yes' : 'No'}
                      </span>
                    }
                    icon={CheckCircle}
                  />
                  <InfoRow
                    label="Payouts Enabled"
                    value={
                      <span className={user.payoutsEnabled ? 'text-emerald-400' : 'text-muted-foreground'}>
                        {user.payoutsEnabled ? 'Yes' : 'No'}
                      </span>
                    }
                    icon={DollarSign}
                  />
                </div>
              </SectionCard>
            </>
          )}

          {/* ============================================================ */}
          {/* BOOKINGS TAB */}
          {/* ============================================================ */}
          {activeTab === 'bookings' && (
            <>
              {/* Booking KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard
                  label="As Customer"
                  value={formatNumber(metrics.bookingsAsCustomerCount)}
                  icon={Calendar}
                  color="text-blue-400"
                  bgColor="bg-blue-400/10"
                  subtitle={`${formatNumber(metrics.completedBookingsAsCustomer)} completed`}
                />
                <MetricCard
                  label="As Provider"
                  value={formatNumber(metrics.bookingsAsProviderCount)}
                  icon={Briefcase}
                  color="text-emerald-400"
                  bgColor="bg-emerald-400/10"
                  subtitle={`${formatNumber(metrics.completedBookingsAsProvider)} completed`}
                />
                <MetricCard
                  label="Total Spent"
                  value={formatCents(metrics.totalSpentCents)}
                  icon={CreditCard}
                  color="text-orange-400"
                  bgColor="bg-orange-400/10"
                />
                <MetricCard
                  label="Total Earned"
                  value={formatCents(metrics.totalEarnedCents)}
                  icon={DollarSign}
                  color="text-emerald-400"
                  bgColor="bg-emerald-400/10"
                />
              </div>

              {/* Bookings list */}
              <SectionCard
                title="Booking History"
                icon={Calendar}
                subtitle={`${formatNumber(bookingsTotal)} total bookings`}
                actions={
                  <div className="flex items-center gap-2">
                    <select
                      value={bookingRole}
                      onChange={(e) => setBookingRole(e.target.value as 'all' | 'customer' | 'provider')}
                      className="px-2 py-1 rounded-md border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      <option value="all">All Roles</option>
                      <option value="customer">Customer</option>
                      <option value="provider">Provider</option>
                    </select>
                    <select
                      value={bookingStatus}
                      onChange={(e) => setBookingStatus(e.target.value)}
                      className="px-2 py-1 rounded-md border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      <option value="">All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="CONFIRMED">Confirmed</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="NO_SHOW">No Show</option>
                      <option value="RECAP">Recap</option>
                    </select>
                    <button
                      onClick={() => refetchBookings()}
                      disabled={bookingsLoading}
                      className="p-1.5 rounded-md border border-border hover:bg-muted/30 transition-colors"
                    >
                      <RefreshCw className={cn('h-3.5 w-3.5 text-muted-foreground', bookingsLoading && 'animate-spin')} />
                    </button>
                  </div>
                }
              >
                {bookingsLoading && bookings.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : bookingsError ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-destructive">{bookingsError}</p>
                  </div>
                ) : bookings.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No bookings found</p>
                  </div>
                ) : (
                  <div>
                    {/* Table header */}
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-border text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      <div className="w-8"></div>
                      <div className="flex-1">Service / Counterparty</div>
                      <div className="w-24">Status</div>
                      <div className="w-20 text-right">Amount</div>
                      <div className="w-14 text-right">Rating</div>
                      <div className="w-28 text-right">Date</div>
                    </div>

                    {/* Rows */}
                    {bookings.map((b) => (
                      <BookingRow
                        key={b.id}
                        booking={b}
                        isSelected={selectedBookingId === b.id}
                        onClick={() => handleBookingClick(b.id)}
                      />
                    ))}

                    {/* Load more */}
                    {bookingsHasMore && (
                      <div className="flex justify-center py-4">
                        <button
                          onClick={loadMoreBookings}
                          disabled={bookingsLoading}
                          className="px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-50"
                        >
                          {bookingsLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            `Load more (${formatNumber(bookingsTotal - bookings.length)} remaining)`
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ============================================================ */}
          {/* CONTENT TAB */}
          {/* ============================================================ */}
          {activeTab === 'content' && (
            <>
              {/* Content KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard
                  label="Total Assets"
                  value={formatNumber(metrics.mediaAssetsCount)}
                  icon={Film}
                  color="text-primary"
                  bgColor="bg-primary/10"
                />
                <MetricCard
                  label="Total Views"
                  value={formatCompactNumber(metrics.contentViewsTotal)}
                  icon={Eye}
                  color="text-blue-400"
                  bgColor="bg-blue-400/10"
                />
                <MetricCard
                  label="Total Likes"
                  value={formatCompactNumber(metrics.contentLikesTotal)}
                  icon={ThumbsUp}
                  color="text-red-400"
                  bgColor="bg-red-400/10"
                />
                <MetricCard
                  label="Avg Views/Asset"
                  value={
                    metrics.mediaAssetsCount > 0
                      ? formatCompactNumber(
                          Math.round(metrics.contentViewsTotal / metrics.mediaAssetsCount)
                        )
                      : '0'
                  }
                  icon={Activity}
                  color="text-emerald-400"
                  bgColor="bg-emerald-400/10"
                />
              </div>

              {/* Content list */}
              <SectionCard
                title="Media Assets"
                icon={Film}
                subtitle={`${formatNumber(contentTotal)} total assets`}
                actions={
                  <div className="flex items-center gap-2">
                    <select
                      value={contentStatus}
                      onChange={(e) => setContentStatus(e.target.value)}
                      className="px-2 py-1 rounded-md border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      <option value="">All Statuses</option>
                      <option value="READY">Ready</option>
                      <option value="PENDING_REVIEW">Pending Review</option>
                      <option value="PENDING_IMPORT">Pending Import</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="UPLOADING">Uploading</option>
                      <option value="FAILED">Failed</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                    <button
                      onClick={() => refetchContent()}
                      disabled={contentLoading}
                      className="p-1.5 rounded-md border border-border hover:bg-muted/30 transition-colors"
                    >
                      <RefreshCw className={cn('h-3.5 w-3.5 text-muted-foreground', contentLoading && 'animate-spin')} />
                    </button>
                  </div>
                }
              >
                {contentLoading && contentItems.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : contentError ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-destructive">{contentError}</p>
                  </div>
                ) : contentItems.length === 0 ? (
                  <div className="text-center py-12">
                    <Film className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No content found</p>
                  </div>
                ) : (
                  <div>
                    {/* Table header */}
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-border text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      <div className="w-4"></div>
                      <div className="w-12"></div>
                      <div className="flex-1">Type / Tag / Caption</div>
                      <div className="w-24">Status</div>
                      <div className="w-28">Engagement</div>
                      <div className="w-14 text-right">Duration</div>
                      <div className="w-24 text-right">Created</div>
                    </div>

                    {contentItems.map((c) => (
                      <ContentRow
                        key={c.id}
                        content={c}
                        expanded={expandedContentId === c.id}
                        onToggle={() => setExpandedContentId(expandedContentId === c.id ? null : c.id)}
                        onRegenerate={handleRegenerate}
                        regenerating={regeneratingId === c.id}
                      />
                    ))}

                    {contentHasMore && (
                      <div className="flex justify-center py-4">
                        <button
                          onClick={loadMoreContent}
                          disabled={contentLoading}
                          className="px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-50"
                        >
                          {contentLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            `Load more (${formatNumber(contentTotal - contentItems.length)} remaining)`
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ============================================================ */}
          {/* SERVICES TAB */}
          {/* ============================================================ */}
          {activeTab === 'services' && (
            <>
              <SectionCard
                title="Listed Services"
                icon={Briefcase}
                subtitle={`${user.services.length} service${user.services.length !== 1 ? 's' : ''}`}
              >
                {user.services.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      This user has no services listed
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 p-4">
                    {user.services.map((svc) => (
                      <ServiceCard key={svc.id} svc={svc} />
                    ))}
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ============================================================ */}
          {/* FINANCIAL TAB */}
          {/* ============================================================ */}
          {activeTab === 'financial' && (
            <FinancialTab
              userId={user.id}
              stripeCustomerId={user.stripeCustomerId}
              stripeAccountId={user.stripeAccountId}
              chargesEnabled={user.chargesEnabled}
              payoutsEnabled={user.payoutsEnabled}
              totalSpentCents={metrics.totalSpentCents}
              totalEarnedCents={metrics.totalEarnedCents}
              completedBookingsAsCustomer={metrics.completedBookingsAsCustomer}
              completedBookingsAsProvider={metrics.completedBookingsAsProvider}
            />
          )}

          {/* ============================================================ */}
          {/* NEW TABS - Lazy-loaded components */}
          {/* ============================================================ */}
          {activeTab === 'household' && <HouseholdTab userId={user.id} />}
          {activeTab === 'availability' && <AvailabilityTab userId={user.id} />}
          {activeTab === 'verification' && <VerificationTab userId={user.id} />}
          {activeTab === 'credentials' && <CredentialsTab userId={user.id} />}
          {activeTab === 'addresses' && <AddressesTab userId={user.id} />}
          {activeTab === 'reports' && <ReportsTab userId={user.id} />}
          {activeTab === 'social' && <SocialTab userId={user.id} />}
          {activeTab === 'referrals' && <ReferralsTab userId={user.id} />}
          {activeTab === 'badges' && <BadgesTab userId={user.id} />}
          {activeTab === 'notifications' && <NotificationsTab userId={user.id} />}
          {activeTab === 'xubux' && <XuBuxTab userId={user.id} />}
          {activeTab === 'communication' && <CommunicationTab userId={user.id} />}
          {activeTab === 'engagement' && <EngagementTab userId={user.id} />}
          {activeTab === 'location' && <LocationTab userId={user.id} />}
          {activeTab === 'devices' && <DevicesAuthTab userId={user.id} />}
          {activeTab === 'recordings' && <RecordingsTab userId={user.id} />}
          {activeTab === 'reviews' && <ReviewsTab userId={user.id} />}
          {activeTab === 'groups' && <GroupSessionsTab userId={user.id} />}
        </div>
      </div>

      {/* Booking Detail Modal */}
      {selectedBookingId != null && selectedBookingListItem && (
        <BookingDetailModal
          booking={selectedBookingListItem}
          fullDetail={bookingDetail}
          loading={bookingDetailLoading}
          error={bookingDetailError}
          onClose={handleCloseBookingDetail}
          onViewUser={handleViewUserFromBooking}
        />
      )}

      {/* Send Notification Modal */}
      {showNotifyModal && (
        <SendNotificationModal
          userId={user.id}
          userName={user.name}
          onClose={() => setShowNotifyModal(false)}
        />
      )}
    </div>
  );
};

export default UserFullView;
